/**
 * Console Helper for FlexDisplay Space State Debugging
 * Provides easy access to state logging from browser console
 */

import stateLogger from './StateLogger';
import { getSpaceStatus, getSpaceColor, SPACE_STATES, COLOR_SCHEMES } from './SpaceColorUtils';

// Enhanced console helper with more debugging features
const consoleHelper = {
  /**
   * Show summary of all space state activity
   */
  summary() {
    return stateLogger.printSummary();
  },

  /**
   * Show recent logs
   * @param {number} limit - Number of logs to show (default: 10)
   * @param {string} type - Filter by type: 'API', 'SOCKET', 'STATE_CHANGE', 'CONNECTION'
   */
  logs(limit = 10, type = null) {
    return stateLogger.printRecentLogs(limit, type);
  },

  /**
   * Monitor a specific space in real-time
   * @param {number} spaceId - Space ID to monitor
   * @returns {function} - Call returned function to stop monitoring
   */
  monitor(spaceId) {
    return stateLogger.monitorSpace(spaceId);
  },

  /**
   * Show only API calls
   */
  api(limit = 10) {
    return stateLogger.printRecentLogs(limit, 'API');
  },

  /**
   * Show only socket updates
   */
  socket(limit = 10) {
    return stateLogger.printRecentLogs(limit, 'SOCKET');
  },

  /**
   * Show only state changes
   */
  changes(limit = 10) {
    return stateLogger.printRecentLogs(limit, 'STATE_CHANGE');
  },

  /**
   * Show connection events
   */
  connections(limit = 10) {
    return stateLogger.printRecentLogs(limit, 'CONNECTION');
  },

  /**
   * Clear all logs
   */
  clear() {
    return stateLogger.clearLogs();
  },

  /**
   * Enable/disable logging
   */
  enable(enabled = true) {
    return stateLogger.setEnabled(enabled);
  },

  /**
   * Get logs for a specific space
   */
  space(spaceId, limit = 10) {
    const logs = stateLogger.getLogs({ spaceId, limit });
    console.group(`🏢 Space ${spaceId} - Last ${logs.length} updates`);
    logs.forEach(log => {
      const time = new Date(log.timestamp).toLocaleTimeString();
      console.log(`[${log.type}] ${log.source} - ${time}`);
      if (log.changes) {
        Object.entries(log.changes).forEach(([sensor, change]) => {
          console.log(`  ${sensor}: ${stateLogger.getStateValue(change.from)} → ${stateLogger.getStateValue(change.to)}`);
        });
      }
    });
    console.groupEnd();
    return logs;
  },

  /**
   * Show help
   */
  help() {
    console.log(`
🔧 FlexDisplay Space Debug Console Commands:

📊 spaceDebug.summary()           - Show state summary
📋 spaceDebug.logs(limit, type)   - Show recent logs
🏢 spaceDebug.space(spaceId)      - Show logs for specific space
👁️ spaceDebug.monitor(spaceId)    - Monitor space in real-time
📡 spaceDebug.api(limit)          - Show API calls only
🔌 spaceDebug.socket(limit)       - Show socket updates only
🔄 spaceDebug.changes(limit)      - Show state changes only
🌐 spaceDebug.connections(limit)  - Show connection events
🎨 spaceDebug.colors(spaceId)     - Show space colors and states
🌈 spaceDebug.colorSchemes(spaceId) - Test different color schemes
📊 spaceDebug.states()            - Show state constants reference
🗑️ spaceDebug.clear()             - Clear all logs
⚙️ spaceDebug.enable(true/false)  - Enable/disable logging
❓ spaceDebug.help()              - Show this help

Examples:
  spaceDebug.logs(20, 'SOCKET')   - Last 20 socket updates
  spaceDebug.space(12345)         - All updates for space 12345
  spaceDebug.colors(12345)        - Show colors for space 12345
  spaceDebug.colors()             - Show colors for first 10 spaces
  spaceDebug.colorSchemes(12345)  - Test color schemes for space
  spaceDebug.states()             - Show state value reference
  const stop = spaceDebug.monitor(12345); stop(); - Monitor then stop
    `);
  },

  /**
   * Show space colors and states
   */
  colors(spaceId = null) {
    // Get signage state from global window if available
    const signageState = window.signageState || 
                        (window.React && window.React.__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED?.ReactCurrentOwner?.current?.memoizedProps?.signageState);
    
    if (!signageState || !signageState.spaces) {
      console.log('❌ No signage state available. Make sure the app is loaded.');
      return;
    }

    const spaces = spaceId ? 
      signageState.spaces.filter(s => s.id === parseInt(spaceId)) : 
      signageState.spaces.slice(0, 10); // Show first 10 if no specific space

    console.group(`🎨 Space Colors ${spaceId ? `for Space ${spaceId}` : '(First 10 spaces)'}`);
    
    spaces.forEach(space => {
      const status = getSpaceStatus(space);
      console.log(
        `%c🏢 ${space.name || space.id} %c${status.name} %c${status.state}`,
        'font-weight: bold;',
        `color: ${status.color}; font-weight: bold; background: ${status.color}20; padding: 2px 6px; border-radius: 3px;`,
        'color: #666; font-size: 0.9em;'
      );
      console.log(`   Sensor: ${status.sensor} | Color: ${status.color} | State: ${status.state}`);
      
      if (space.senses) {
        Object.entries(space.senses).forEach(([sensor, data]) => {
          if (data && typeof data.state !== 'undefined') {
            console.log(`   ${sensor}: ${data.state} (${new Date(data.timestamp).toLocaleTimeString()})`);
          }
        });
      }
    });
    
    console.groupEnd();
    return spaces.map(s => getSpaceStatus(s));
  },

  /**
   * Test different color schemes
   */
  colorSchemes(spaceId) {
    const signageState = window.signageState || 
                        (window.React && window.React.__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED?.ReactCurrentOwner?.current?.memoizedProps?.signageState);
    
    if (!signageState || !signageState.spaces) {
      console.log('❌ No signage state available.');
      return;
    }

    const space = signageState.spaces.find(s => s.id === parseInt(spaceId));
    if (!space) {
      console.log(`❌ Space ${spaceId} not found.`);
      return;
    }

    console.group(`🎨 Color Schemes for Space ${space.name || space.id}`);
    
    Object.keys(COLOR_SCHEMES).forEach(schemeName => {
      const color = getSpaceColor(space, schemeName);
      const status = getSpaceStatus(space, schemeName);
      console.log(
        `%c${schemeName}: ${status.name}`,
        `color: ${color}; font-weight: bold; background: ${color}20; padding: 2px 8px; border-radius: 3px; margin-right: 10px;`
      );
    });
    
    console.groupEnd();
  },

  /**
   * Show state constants reference
   */
  states() {
    console.group('📊 Space State Reference');
    console.log('State Constants:');
    Object.entries(SPACE_STATES).forEach(([name, value]) => {
      console.log(`  ${name}: ${value}`);
    });
    
    console.log('\nColor Mapping (Default Scheme):');
    Object.entries(COLOR_SCHEMES.default).forEach(([state, color]) => {
      const stateName = Object.keys(SPACE_STATES).find(key => SPACE_STATES[key] === parseInt(state));
      console.log(
        `%c  ${stateName} (${state}): ${color}`,
        `color: ${color}; font-weight: bold; background: ${color}20; padding: 2px 6px; border-radius: 3px;`
      );
    });
    console.groupEnd();
  },

  /**
   * Live dashboard - continuously show updates
   */
  dashboard() {
    console.clear();
    console.log('🔄 Starting live dashboard... (Press Ctrl+C to stop)');
    
    const showDashboard = () => {
      console.clear();
      console.log('📊 FlexDisplay Live Dashboard');
      console.log('═'.repeat(50));
      
      const summary = stateLogger.getStateSummary();
      console.log(`📈 Total Logs: ${summary.totalLogs}`);
      console.log(`🔗 API Calls: ${summary.apiUpdates}`);
      console.log(`📡 Socket Updates: ${summary.socketUpdates}`);
      console.log(`🔄 State Changes: ${summary.stateChanges}`);
      console.log(`🏢 Spaces Affected: ${summary.spacesAffected}`);
      console.log(`⏰ Last Update: ${summary.lastUpdate ? new Date(summary.lastUpdate).toLocaleTimeString() : 'Never'}`);
      console.log('═'.repeat(50));
      
      const recentLogs = stateLogger.getLogs({ limit: 5 });
      console.log('📋 Recent Activity:');
      recentLogs.forEach(log => {
        const time = new Date(log.timestamp).toLocaleTimeString();
        const icon = {
          'API': '📡',
          'SOCKET': '🔌',
          'STATE_CHANGE': '🔄',
          'CONNECTION': '🌐'
        }[log.type] || '📝';
        
        console.log(`${icon} [${time}] ${log.source} ${log.spaceId ? `(Space: ${log.spaceId})` : ''}`);
      });
      
      console.log('\n💡 Type spaceDebug.help() for commands');
    };

    showDashboard();
    const interval = setInterval(showDashboard, 5000);
    
    // Return function to stop dashboard
    return () => {
      clearInterval(interval);
      console.log('⏹️ Dashboard stopped');
    };
  }
};

// Make available globally
if (typeof window !== 'undefined') {
  window.spaceDebug = consoleHelper;
  console.log('🔧 FlexDisplay Space Debug Console loaded!');
  console.log('💡 Type spaceDebug.help() for available commands');
  console.log('📊 Type spaceDebug.summary() for current state');
  console.log('📋 Type spaceDebug.logs() for recent activity');
}

export default consoleHelper;
